import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ThumbsUp,
  ThumbsDown,
  Edit2,
  Save,
  X,
  ArrowUpDown,
  CheckCircle,
  AlertCircle,
  MessageSquare
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Question {
  id: string;
  question_text: string;
  answer_type: string;
  search_volume?: number;
  opportunity_score?: number;
  answer_id?: string;
  answer?: string;
  vote?: 'upvote' | 'downvote' | 'neutral';
  feedback?: string;
  voted_at?: string;
  source_type?: string;
  confidence_score?: number;
  approval_status?: string;
}

interface QuestionPreviewWithAnswersProps {
  questions: Question[];
  wheelId: string;
  category: string;
  onRefresh: () => void;
  onSelectQuestion?: (questionId: string) => void;
  selectedQuestions?: Set<string>;
  onQuestionsUpdate?: (questions: Question[]) => void;
}

export function QuestionPreviewWithAnswers({
  questions,
  wheelId,
  category,
  onRefresh,
  onSelectQuestion,
  selectedQuestions,
  onQuestionsUpdate
}: QuestionPreviewWithAnswersProps) {
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null);
  const [editedAnswer, setEditedAnswer] = useState<string>('');
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [votingAnswerId, setVotingAnswerId] = useState<string | null>(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [sortedByQuestionId, setSortedByQuestionId] = useState<string | null>(null);
  const [sortingBySimilarity, setSortingBySimilarity] = useState(false);
  const [pendingVote, setPendingVote] = useState<{ answerId: string; vote: string } | null>(null);

  const handleStartEdit = (answerId: string, currentAnswer: string) => {
    setEditingAnswerId(answerId);
    setEditedAnswer(currentAnswer);
  };

  const handleCancelEdit = () => {
    setEditingAnswerId(null);
    setEditedAnswer('');
  };

  const handleSaveEdit = async (answerId: string) => {
    try {
      setSavingAnswer(true);
      await authFetch(`/api/answer-generation/answers/${answerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: editedAnswer })
      });

      setEditingAnswerId(null);
      setEditedAnswer('');
      onRefresh();
    } catch (error) {
      console.error('Error saving answer:', error);
      alert('Failed to save answer');
    } finally {
      setSavingAnswer(false);
    }
  };

  const handleVote = async (answerId: string, vote: 'upvote' | 'downvote' | 'neutral', feedback?: string) => {
    try {
      setVotingAnswerId(answerId);
      await authFetch(`/api/answer-generation/answers/${answerId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote, feedback: feedback || null })
      });

      onRefresh();
    } catch (error) {
      console.error('Error voting on answer:', error);
      alert('Failed to record vote');
    } finally {
      setVotingAnswerId(null);
      setFeedbackDialogOpen(false);
      setFeedbackText('');
      setPendingVote(null);
    }
  };

  const handleDownvoteClick = (answerId: string) => {
    setPendingVote({ answerId, vote: 'downvote' });
    setFeedbackDialogOpen(true);
  };

  const handleFeedbackSubmit = () => {
    if (pendingVote) {
      handleVote(pendingVote.answerId, pendingVote.vote as any, feedbackText);
    }
  };

  const handleSortBySimilarity = async (questionId: string) => {
    try {
      setSortingBySimilarity(true);
      const response = await authFetch(
        `${API_URL}/question-wheels/${wheelId}/questions-by-similarity?questionId=${questionId}&category=${category}&limit=100`
      );
      const data = await response.json();

      if (data.success && data.questions) {
        // The API returns questions sorted by similarity with the reference question first
        setSortedByQuestionId(questionId);
        // Update the parent's questions list with sorted questions
        if (onQuestionsUpdate) {
          onQuestionsUpdate(data.questions);
        }
      }
    } catch (error) {
      console.error('Error sorting by similarity:', error);
      alert('Failed to sort by similarity');
    } finally {
      setSortingBySimilarity(false);
    }
  };

  const handleClearSimilaritySort = () => {
    setSortedByQuestionId(null);
    onRefresh();
  };

  if (questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            No questions found in this category.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Questions & Answers</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {questions.length} questions • {questions.filter(q => q.answer).length} with answers
          </p>
        </div>
        {onSelectQuestion && (
          <div className="text-sm text-muted-foreground">
            {selectedQuestions?.size || 0} selected
          </div>
        )}
      </div>

      {sortedByQuestionId && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-blue-900 dark:text-blue-200">
              Questions sorted by similarity to selected question
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={handleClearSimilaritySort}>
            <X className="h-3 w-3 mr-1" />
            Clear Sort
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {questions.map((question) => (
          <Card key={question.id} className={`${selectedQuestions?.has(question.id) ? 'ring-2 ring-primary' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  {onSelectQuestion && (
                    <Checkbox
                      checked={selectedQuestions?.has(question.id)}
                      onCheckedChange={() => onSelectQuestion(question.id)}
                      className="mt-1"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base font-medium flex-1">
                        {question.question_text}
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSortBySimilarity(question.id)}
                        disabled={sortingBySimilarity || sortedByQuestionId === question.id}
                        className="shrink-0"
                      >
                        <ArrowUpDown className="h-3 w-3 mr-1" />
                        {sortedByQuestionId === question.id ? 'Reference' : 'Sort Similar'}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {question.answer_type?.replace('_', ' ')}
                      </Badge>
                      {question.search_volume && (
                        <Badge variant="secondary" className="text-xs">
                          {question.search_volume} searches/mo
                        </Badge>
                      )}
                      {question.opportunity_score && (
                        <Badge variant="secondary" className="text-xs">
                          Score: {question.opportunity_score}
                        </Badge>
                      )}
                      {sortedByQuestionId === question.id && (
                        <Badge variant="default" className="text-xs">
                          Reference Question
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>

            {question.answer && (
              <CardContent className="pt-0 space-y-3">
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-muted-foreground">Answer</Label>
                    <div className="flex items-center gap-2">
                      {question.approval_status && (
                        <Badge variant={question.approval_status === 'approved' ? 'default' : 'secondary'}>
                          {question.approval_status}
                        </Badge>
                      )}
                      {!editingAnswerId && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEdit(question.answer_id!, question.answer!)}
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>

                  {editingAnswerId === question.answer_id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="min-h-[100px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveEdit(question.answer_id!)}
                          disabled={savingAnswer}
                        >
                          <Save className="h-3 w-3 mr-1" />
                          {savingAnswer ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={savingAnswer}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                      {question.answer}
                    </p>
                  )}

                  {/* Voting Section */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={question.vote === 'upvote' ? 'default' : 'outline'}
                        onClick={() => handleVote(question.answer_id!, 'upvote')}
                        disabled={votingAnswerId === question.answer_id}
                      >
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        {question.vote === 'upvote' ? 'Upvoted' : 'Good'}
                      </Button>
                      <Button
                        size="sm"
                        variant={question.vote === 'downvote' ? 'destructive' : 'outline'}
                        onClick={() => handleDownvoteClick(question.answer_id!)}
                        disabled={votingAnswerId === question.answer_id}
                      >
                        <ThumbsDown className="h-3 w-3 mr-1" />
                        {question.vote === 'downvote' ? 'Downvoted' : 'Bad'}
                      </Button>
                    </div>

                    {question.vote === 'downvote' && question.feedback && (
                      <div className="flex items-start gap-2 flex-1 bg-red-50 dark:bg-red-900/20 p-2 rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                        <div className="text-xs text-red-800 dark:text-red-200">
                          <strong>Feedback:</strong> {question.feedback}
                        </div>
                      </div>
                    )}

                    {question.vote === 'upvote' && (
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>Marked as good answer</span>
                      </div>
                    )}
                  </div>

                  {question.confidence_score && (
                    <div className="text-xs text-muted-foreground mt-2">
                      Confidence: {(question.confidence_score * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              </CardContent>
            )}

            {!question.answer && (
              <CardContent className="pt-0">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    No answer generated yet. Start generation to create an answer for this question.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Feedback Dialog */}
      <AlertDialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Provide Feedback</AlertDialogTitle>
            <AlertDialogDescription>
              Please explain why this answer is not good. Your feedback helps improve future answer generation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="E.g., The answer is inaccurate, missing key information, too generic, etc."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setFeedbackDialogOpen(false);
              setFeedbackText('');
              setPendingVote(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleFeedbackSubmit}>
              Submit Feedback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
